import { Button, Layout, Tag, Text } from '@loadsmart/miranda-react'
import { getToken } from '@loadsmart/miranda-react/dist/tokens'

import { formatLocation, formatStopAppointmentDate } from 'core/presenters/stopPresenter'
import { CardInfoItem } from 'core/ui/components/card/CardInfoItem'
import { VerticalDivider } from 'core/ui/components/divider/VerticalDivider'
import { ShipperInformation } from 'core/ui/components/shipper/ShipperInformation'

import { formatEquipment, getAppointmentStatus } from '../presenters/agnosticShipmentPresenter'
import { formatCommodities } from '../presenters/baseShipmentPresenter'
import { LastLocation } from './LastLocation'
import { type ShipmentCardProps } from './ShipmentCard'
import { ShipmentIdentifiers } from './ShipmentIdentifiers'
import { StopInformation } from './StopInformation'

export function ShipmentCardDesktop({
  shipment,
  getActionLabel,
  getIdentifiers,
  getStatus,
  onActionClick,
}: ShipmentCardProps) {
  const actionLabel = getActionLabel?.()
  const identifiers = getIdentifiers?.()
  const { label, variant } = getStatus?.() ?? {}

  const {
    equipmentType,
    mileage,
    mode,
    shipper,
    stops,
    equipmentLength,
    equipmentSubType,
    poNumbers,
    soNumbers,
  } = shipment
  const commodities = formatCommodities(shipment)

  const appointmentStatus = getAppointmentStatus(shipment)

  function handleOnActionClick(event: React.MouseEvent) {
    event.stopPropagation()
    onActionClick?.()
  }

  return (
    <Layout.Box
      backgroundColor="color-background-primary"
      borderRadius="border-radius-s"
      borderWidth="border-thin"
      borderColor="color-border"
      padding="none"
      style={{ display: 'block' }}
      data-testid="shipment-card-desktop"
    >
      <Layout.Group style={{ padding: getToken('spacing-6'), flexWrap: 'nowrap' }}>
        <Layout.Stack gap="spacing-6" style={{ flexGrow: 1 }}>
          <ShipmentIdentifiers identifiers={identifiers} />
          <Layout.Group justify="space-between" align="center" style={{ flexWrap: 'nowrap' }}>
            <StopInformation
              stops={stops}
              distance={mileage}
              locationFormatter={formatLocation}
              dateFormatter={formatStopAppointmentDate}
              distanceUnit="miles"
            />
            <Layout.Stack align="flex-end" justify="space-between">
              <Layout.Group justify="space-between" align="center" style={{ flexWrap: 'nowrap' }}>
                {appointmentStatus && (
                  <Text
                    style={{ whiteSpace: 'nowrap' }}
                    variant="body-md-bold"
                    color="color-text-error"
                  >
                    {appointmentStatus}
                  </Text>
                )}
                <Tag variant={variant}>{label}</Tag>
              </Layout.Group>
              {actionLabel && (
                <Button
                  variant="secondary"
                  type="button"
                  onClick={handleOnActionClick}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {actionLabel}
                </Button>
              )}
            </Layout.Stack>
          </Layout.Group>
          <Layout.Group gap="spacing-6" style={{ flexWrap: 'nowrap' }}>
            <ShipperInformation
              companyName={shipper.companyName}
              logo={shipper.logoUrl}
              showContactInfo={false}
            />
            <VerticalDivider />
            <CardInfoItem title="Mode" value={mode} />
            <VerticalDivider />
            <CardInfoItem
              title="Equipment"
              value={formatEquipment(equipmentType, equipmentLength)}
            />
            {equipmentSubType && (
              <>
                <VerticalDivider />
                <CardInfoItem title="Sub Equipment" value={equipmentSubType} />
              </>
            )}
            {poNumbers && (
              <>
                <VerticalDivider />
                <CardInfoItem
                  style={{ maxWidth: '110px', flexWrap: 'wrap', overflowWrap: 'anywhere' }}
                  title="PO#"
                  value={poNumbers.join(', ')}
                />
              </>
            )}
            {soNumbers && (
              <>
                <VerticalDivider />
                <CardInfoItem
                  style={{ maxWidth: '110px', flexWrap: 'wrap', overflowWrap: 'anywhere' }}
                  title="SO#"
                  value={soNumbers.join(', ')}
                />
              </>
            )}
            {commodities && (
              <>
                <VerticalDivider />
                <CardInfoItem title="Commodity" value={commodities} />
              </>
            )}
          </Layout.Group>
        </Layout.Stack>
      </Layout.Group>
      <LastLocation shipment={shipment} />
    </Layout.Box>
  )
}
